import React, { useMemo } from 'react';
import { TextResponse } from '../../types/analytics';

interface WordCloudWidgetProps {
  responses: TextResponse[];
}

const STOP_WORDS = new Set([
  'the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had', 'her',
  'was', 'one', 'our', 'out', 'has', 'have', 'been', 'some', 'them', 'than',
  'its', 'over', 'also', 'that', 'other', 'into', 'more', 'very', 'with',
  'they', 'this', 'from', 'will', 'each', 'make', 'like', 'just', 'about',
  'would', 'there', 'their', 'what', 'when', 'who', 'how', 'which', 'she',
  'does', 'these', 'him', 'his', 'could', 'should', 'did', 'get', 'may',
  'were', 'then', 'being', 'those', 'much', 'well', 'only', 'any', 'after',
]);

const COLORS = [
  'text-blue-600 dark:text-blue-400',
  'text-emerald-600 dark:text-emerald-400',
  'text-amber-600 dark:text-amber-400',
  'text-rose-600 dark:text-rose-400',
  'text-violet-600 dark:text-violet-400',
];

const WordCloudWidget: React.FC<WordCloudWidgetProps> = ({ responses }) => {
  const words = useMemo(() => {
    const counts = new Map<string, number>();

    for (const response of responses) {
      const tokens = response.text.split(/\W+/).filter((w) => w.length > 2);
      for (const token of tokens) {
        const lower = token.toLowerCase();
        if (!STOP_WORDS.has(lower)) {
          counts.set(lower, (counts.get(lower) || 0) + 1);
        }
      }
    }

    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([text, value], index) => ({ text, value, color: COLORS[index] }));
  }, [responses]);

  if (!words.length) return null;

  const maxValue = words[0].value;
  const minValue = words[words.length - 1].value;
  const range = maxValue - minValue || 1;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
      <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Top words:</span>
      {words.map((word) => {
        const fontSize = 0.8 + ((word.value - minValue) / range) * 0.7;
        return (
          <span
            key={word.text}
            className={`font-semibold ${word.color}`}
            style={{ fontSize: `${fontSize}rem` }}
            title={`${word.text}: ${word.value}`}
          >
            {word.text}
          </span>
        );
      })}
    </div>
  );
};

export default WordCloudWidget;
